import { WalkthroughSection } from '../context/WalkthroughContext';

export interface UniqueSection {
  id: string;
  title: string;
  description: string;
  icon: string;
}

export interface WalkthroughStep {
  id: string;
  section: string | WalkthroughSection;
  title: string;
  description: string;
  route: string;
  targetSelector?: string;
  action?: 'click' | 'view' | 'type' | 'navigate';
  actionText?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  waitForElement?: boolean;
  skipHighlight?: boolean;
  uniqueSections?: UniqueSection[];
}

export const walkthroughSteps: WalkthroughStep[] = [
  {
    id: 'welcome',
    section: 'init',
    title: 'Welcome to OpenANA Security! 👋',
    description: 'Let\'s take an interactive tour of your security platform. I\'ll guide you through the key features step by step.',
    route: '/dashboard',
    action: 'navigate',
    actionText: 'Start the guided tour',
    uniqueSections: [
      {
        id: 'dashboard-projects',
        title: 'Dashboard & Projects',
        description: 'Explore your security metrics and manage projects',
        icon: '📊'
      },
      {
        id: 'integration',
        title: 'Integrations',
        description: 'Connect repositories and security tools',
        icon: '🔌'
      },
      {
        id: 'scan-using-ana',
        title: 'Security Scanning',
        description: 'Run different types of security scans',
        icon: '🔍'
      },
      {
        id: 'fix-by-ana',
        title: 'Auto-Fix Issues',
        description: 'Let ANA fix security vulnerabilities',
        icon: '🛠️'
      },
      {
        id: 'ask-ana',
        title: 'Ask ANA',
        description: 'Chat with your AI security expert',
        icon: '💬'
      }
    ]
  },
  {
    id: 'dashboard-metrics',
    section: 'dashboard-projects',
    title: 'Security Metrics Overview',
    description: 'These cards show your security overview: total projects, scans completed, and issues found. This gives you a quick health check of your security posture.',
    route: '/dashboard',
    targetSelector: '[class*="grid"][class*="grid-cols-1"][class*="md:grid-cols-2"]:first-of-type',
    action: 'view',
    actionText: 'Look at your security metrics',
    position: 'top'
  },
  {
    id: 'dashboard-chart',
    section: 'dashboard-projects',
    title: 'Scan Categories Breakdown',
    description: 'This chart shows your scanning activity across different security modules. Each color represents a different type of security scan.',
    route: '/dashboard',
    targetSelector: '[class*="w-full"][class*="lg:w-1/2"]:last-of-type',
    action: 'view',
    actionText: 'Explore the scan categories',
    position: 'left'
  },
  {
    id: 'dashboard-issues-table',
    section: 'dashboard-projects',
    title: 'Issues by Scan Type',
    description: 'This table breaks down security issues by severity (Critical, High, Medium, Low) for each scan type. Focus on Critical and High severity issues first.',
    route: '/dashboard',
    targetSelector: '[class*="w-full"][class*="lg:w-\\[60\\%\\]"]',
    action: 'view',
    actionText: 'Review your security issues',
    position: 'right'
  },
  {
    id: 'projects-navigation',
    section: 'dashboard-projects',
    title: 'Navigate to Projects',
    description: 'Now let\'s explore your projects. Click on "Projects" in the sidebar to see all your repositories and applications.',
    route: '/dashboard',
    targetSelector: 'a[href="/projects"]',
    action: 'click',
    actionText: 'Click on Projects in the sidebar',
    position: 'right'
  },
  {
    id: 'projects-overview',
    section: 'dashboard-projects',
    title: 'Your Security Projects',
    description: 'Here are all your projects under security management. Each card shows project details, repository links, and scan history.',
    route: '/projects',
    targetSelector: '.grid [data-testid="project-card"]:first-child',
    action: 'view',
    actionText: 'Browse your projects', 
    position: 'right',
  },
  {
    id: 'new-project-button',
    section: 'dashboard-projects',
    title: 'Create a New Project',
    description: 'To add a new repository for security scanning, click the "New Project" button. This will open a form to connect your repository.',
    route: '/projects',
    targetSelector: 'button[class*="walkthrough-new-project-btn"]',
    action: 'click',
    actionText: 'Click "New Project" to add a repository',
    position: 'bottom'
  },
  {
    id: 'project-form',
    section: 'dashboard-projects',
    title: 'Project Creation Form',
    description: 'Great! The project creation form is open. Let\'s fill it out step by step to connect your repository to OpenANA Security.',
    route: '/projects',
    targetSelector: '[role="dialog"], .MuiDialog-paper',
    action: 'view',
    actionText: 'Review the project creation form',
    position: 'left',
    waitForElement: true
  },
  {
    id: 'integrations-navigation',
    section: 'integration',
    title: 'Repository Integrations',
    description: 'Let\'s set up integrations. Click on "Integration" in the sidebar to connect your repositories for automatic security fixes.',
    route: '/projects',
    targetSelector: 'a[href="/integration"]',
    action: 'click',
    actionText: 'Click on Integration in the sidebar',
    position: 'right'
  },
  {
    id: 'github-integration',
    section: 'integration',
    title: 'GitHub Integration',
    description: 'Connect your GitHub repositories here. This enables ANA to automatically create pull requests with security fixes when issues are found.',
    route: '/integration',
    targetSelector: '[data-walkthrough="github-integration"]',
    action: 'view',
    actionText: 'See GitHub integration options',
    position: 'bottom'
  },
  {
    id: 'third-party-tools',
    section: 'integration',
    title: 'Security Tool Integrations',
    description: 'These are third-party security tools that integrate with OpenANA. Connect tools like Checkmarx, Invicti, and Wiz for comprehensive security coverage.',
    route: '/integration',
    targetSelector: '[data-walkthrough="third-party-tools"]',
    action: 'view',
    actionText: 'Explore security tool integrations',
    position: 'top'
  },
  {
    id: 'sast-navigation',
    section: 'fix-by-ana',
    title: 'SAST Code Scanning',
    description: 'Now let\'s explore code security. Click on "SAST" in the sidebar to see static code analysis and security vulnerabilities.',
    route: '/integration',
    targetSelector: 'a[href="/sast"]',
    action: 'click',
    actionText: 'Click on SAST in the sidebar',
    position: 'right'
  },
  {
    id: 'sast-dashboard',
    section: 'fix-by-ana',
    title: 'SAST Dashboard Overview',
    description: 'This shows your static code analysis results: total scans, identified fixes, and completed fixes. The metrics help track your code security progress.',
    route: '/sast',
    targetSelector: '[data-walkthrough="sast-dashboard"]',
    action: 'view',
    actionText: 'Review your SAST metrics',
    position: 'bottom'
  },
  {
    id: 'sast-scan-history',
    section: 'fix-by-ana',
    title: 'Scan History & Results',
    description: 'Here you can see all your previous SAST scans. Click on any scan to view detailed results and security findings.',
    route: '/sast',
    targetSelector: '[class*="space-y-4"], table, [class*="overflow-auto"]',
    action: 'click',
    actionText: 'Click on any scan to view details',
    position: 'top'
  },
  {
    id: 'sast-scan-details',
    section: 'fix-by-ana',
    title: 'Scan Details & Findings',
    description: 'Now you can see the detailed scan results. Click on any scan item to open the dropdown that contains fix options.',
    route: '/sast/scan/:id',
    targetSelector: '[data-walkthrough="scan-item"]',
    action: 'click',
    actionText: 'Click on a scan item to see fix options',
    position: 'top',
    waitForElement: true
  },
  {
    id: 'sast-fix-buttons',
    section: 'fix-by-ana',
    title: 'ANA Actions - Auto Fix',
    description: 'Look for orange "Fix" buttons in scan results! These allow ANA to automatically create pull requests that fix security issues in your code.',
    route: '/sast/scan/:id', // Using a dynamic route pattern that will be replaced with the actual scan ID
    targetSelector: '[data-walkthrough="fix-button"]',
    action: 'view',
    actionText: 'Find the Fix buttons for automatic remediation',
    position: 'left',
    waitForElement: true
  },
  {
    id: 'ask-ana-navigation',
    section: 'ask-ana',
    title: 'Meet Ask ANA - Your AI Assistant',
    description: 'Let\'s explore Ask ANA, your AI-powered security assistant. Click on "Ask Ana" in the sidebar to get expert security guidance.',
    route: '/sast',
    targetSelector: 'a[href*="ask"], [href*="ana"]',
    action: 'click',
    actionText: 'Click on Ask Ana in the sidebar',
    position: 'right'
  },
  {
    id: 'ask-ana-chat',
    section: 'ask-ana',
    title: 'Start a Security Conversation',
    description: 'Type a security question in the chat box below. Try asking "How do I fix SQL injection vulnerabilities?" or "What are the most critical security issues in my code?"',
    route: '/ask-ana',
    targetSelector: 'input[placeholder*="Ask"], textarea[placeholder*="Ask"], [class*="input"]',
    action: 'type',
    actionText: 'Type "Hey" or ask a security question',
    position: 'top'
  },
  {
    id: 'ask-ana-history',
    section: 'ask-ana',
    title: 'Chat History & New Conversations',
    description: 'Use these buttons to manage your conversations. The menu icon shows your chat history, while the plus icon starts a new conversation. ANA remembers your previous questions and can provide contextual help.',
    route: '/ask-ana',
    targetSelector: '[data-walkthrough="chat-controls"]',
    action: 'view',
    actionText: 'Click to see chat options',
    position: 'right'
  },
  {
    id: 'sbom-navigation',
    section: 'scan-using-ana',
    title: 'Access the SBOM Feature',
    description: 'Navigate to the SBOM section to begin generating a Software Bill of Materials for your project. This section is dedicated to managing and exporting SBOMs.',
    route: '/sbom',
    targetSelector: '[class*="sidebar"] [class*="sbom-menu-item"]',
    action: 'click',
    actionText: 'Click "SBOM" in the sidebar',
    position: 'right'
  },
  {
    id: 'new-sbom-scan',
    section: 'scan-using-ana',
    title: 'Start a New SBOM Scan',
    description: 'To scan your code for security vulnerabilities, click "New Scan". This will analyze your source code for potential security issues.',
    route: '/sbom',
    targetSelector: 'button[data-walkthrough="new-sast-scan-btn"]',
    action: 'click',
    actionText: 'Click "New Scan" to analyze your code',
    position: 'bottom'
  },
  {
    id: 'select-project-sbom',
    section: 'scan-using-ana',
    title: 'Select a Project',
    description: 'Choose the project for which you want to generate an SBOM. You can use the search or select directly from the list.',
    route: '/sbom',
    targetSelector: '[role="dialog"], .MuiDialog-paper',
    action: 'click',
    actionText: 'Select your target project',
    position: 'right',
    waitForElement: true
  },
  {
    id: 'generate-sbom-button',
    section: 'scan-using-ana',
    title: 'Generate the SBOM',
    description: 'Click the "Generate SBOM" button to start the process. The app will analyze the project dependencies and assemble the SBOM.',
    route: '/sbom',
    targetSelector: 'button[data-walkthrough="generate-sbom-btn"]',
    action: 'click',
    actionText: 'Click "Generate SBOM"',
    position: 'right',
    waitForElement: true
  },
  {
    id: 'tour-complete',
    section: 'complete',
    title: 'Tour Complete! 🎉',
    description: 'You\'ve explored the key features of OpenANA Security! Start by running scans, connecting repositories, and using ANA Actions to fix security issues automatically.',
    route: '/dashboard',
    action: 'navigate',
    actionText: 'Start securing your applications!',
    skipHighlight: true
  }
];

// Unique section walkthrough steps
export const uniqueSectionSteps: Record<WalkthroughSection, WalkthroughStep[]> = {
  'dashboard-projects': walkthroughSteps.filter(step => step.section === 'dashboard-projects'),
  'integration': walkthroughSteps.filter(step => step.section === 'integration'),
  'scan-using-ana': walkthroughSteps.filter(step => step.section === 'scan-using-ana'),
  'fix-by-ana': walkthroughSteps.filter(step => step.section === 'fix-by-ana'),
  'ask-ana': walkthroughSteps.filter(step => step.section === 'ask-ana')
};

// Welcome card for each unique section
export const sectionWelcomeCards: Record<WalkthroughSection, WalkthroughStep> = {
  'dashboard-projects': {
    id: 'dashboard-projects-welcome',
    section: 'dashboard-projects',
    title: 'Explore Your Security Dashboard & Projects',
    description: 'Let\'s explore your security dashboard and learn how to manage projects in OpenANA Security.',
    route: '/dashboard',
    action: 'navigate',
    actionText: 'Start exploring dashboard & projects',
    skipHighlight: true
  },
  'integration': {
    id: 'integration-welcome',
    section: 'integration',
    title: 'Connect Your Repositories & Tools',
    description: 'Learn how to integrate your repositories and third-party security tools with OpenANA Security.',
    route: '/integration',
    action: 'navigate',
    actionText: 'Start integration walkthrough',
    skipHighlight: true
  },
  'scan-using-ana': {
    id: 'scan-welcome',
    section: 'scan-using-ana',
    title: 'Run Security Scans with ANA',
    description: 'Learn how to run different types of security scans to identify vulnerabilities in your applications.',
    route: '/dashboard',
    action: 'navigate',
    actionText: 'Start scanning walkthrough',
    skipHighlight: true
  },
  'fix-by-ana': {
    id: 'fix-welcome',
    section: 'fix-by-ana',
    title: 'Fix Security Issues Automatically',
    description: 'Discover how ANA can automatically fix security vulnerabilities in your code with AI-powered remediation.',
    route: '/sast',
    action: 'navigate',
    actionText: 'Start fix walkthrough',
    skipHighlight: true
  },
  'ask-ana': {
    id: 'ask-welcome',
    section: 'ask-ana',
    title: 'Chat with Your AI Security Expert',
    description: 'Learn how to use Ask ANA, your AI security assistant, to get expert guidance on security best practices.',
    route: '/ask-ana',
    action: 'navigate',
    actionText: 'Start Ask ANA walkthrough',
    skipHighlight: true
  }
};

// Completion card for each unique section
export const sectionCompletionCards: Record<WalkthroughSection, WalkthroughStep> = {
  'dashboard-projects': {
    id: 'dashboard-projects-complete',
    section: 'dashboard-projects',
    title: 'Dashboard & Projects Tour Complete! 🎉',
    description: 'You\'ve explored the Dashboard & Projects section. Would you like to continue to the next section or finish the tour?',
    route: '/dashboard',
    action: 'navigate',
    actionText: 'Continue to Integrations',
    skipHighlight: true
  },
  'integration': {
    id: 'integration-complete',
    section: 'integration',
    title: 'Integrations Tour Complete! 🎉',
    description: 'You\'ve explored the Integrations section. Would you like to continue to the next section or finish the tour?',
    route: '/integration',
    action: 'navigate',
    actionText: 'Continue to Security Scanning',
    skipHighlight: true
  },
  'scan-using-ana': {
    id: 'scan-complete',
    section: 'scan-using-ana',
    title: 'Security Scanning Tour Complete! 🎉',
    description: 'You\'ve explored the Security Scanning section. Would you like to continue to the next section or finish the tour?',
    route: '/dashboard',
    action: 'navigate',
    actionText: 'Continue to Auto-Fix Issues',
    skipHighlight: true
  },
  'fix-by-ana': {
    id: 'fix-complete',
    section: 'fix-by-ana',
    title: 'Auto-Fix Issues Tour Complete! 🎉',
    description: 'You\'ve explored the Auto-Fix Issues section. Would you like to continue to the next section or finish the tour?',
    route: '/sast',
    action: 'navigate',
    actionText: 'Continue to Ask ANA',
    skipHighlight: true
  },
  'ask-ana': {
    id: 'ask-complete',
    section: 'ask-ana',
    title: 'Ask ANA Tour Complete! 🎉',
    description: 'You\'ve explored the Ask ANA section. You\'ve now completed all sections of the guided tour!',
    route: '/ask-ana',
    action: 'navigate',
    actionText: 'Finish Tour',
    skipHighlight: true
  }
};
